'use client';

import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { use, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useIsMobile } from '@/hooks/useIsMobile';
import { isIOS, isStandaloneMode } from '@/lib/device';
import { IosNotificationInstructionsDialogContext } from '../context/IosNotificationInstructionsDialogContext';
import { PushNotificationContext } from '../context/PushNotificationContext';

const PUSH_NOTIFICATION_DIALOG_DISMISSED_KEY =
  'pushNotificationDialogDismissed';

export function PushNotificationSubscriberDialog() {
  const t = useTranslations('notifications');
  const pushNotification = use(PushNotificationContext);
  if (!pushNotification) throw new Error('PushNotificationContext not found');
  const { subscribeToPush, isSubscribing } = pushNotification;
  const [isOpen, setIsOpen] = useState(false);
  const router = useRouter();
  const [isMobile, isMobileLoading] = useIsMobile();

  const { showIosPopup } = use(IosNotificationInstructionsDialogContext);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    const showPushDialog = url.searchParams.get('showPushDialog');
    if (
      typeof window !== 'undefined' &&
      'Notification' in window &&
      Notification.permission === 'default' &&
      !localStorage.getItem(PUSH_NOTIFICATION_DIALOG_DISMISSED_KEY)
    ) {
      if (
        (isIOS() && isStandaloneMode()) ||
        (!isMobileLoading && isMobile && showPushDialog === '1' && !isIOS())
      ) {
        setIsOpen(true);
      } else if (showPushDialog === '1' && isIOS()) {
        showIosPopup();
      }
    }
  }, [isMobile, showIosPopup, isMobileLoading]);

  const handleSubscribe = async () => {
    const result = await subscribeToPush();
    if (result.success) {
      handleOpenClose(false);
      router.push('/login');
    }
  };

  const handleOpenClose = (isOpen: boolean) => {
    if (!isOpen) {
      localStorage.setItem(PUSH_NOTIFICATION_DIALOG_DISMISSED_KEY, 'true');
    }
    setIsOpen(isOpen);
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('dialogs.pushNotifications.title')}</DialogTitle>
          <DialogDescription>
            {t('dialogs.pushNotifications.description')}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant='tertiary'
            onClick={() => handleOpenClose(false)}
            disabled={isSubscribing}
          >
            {t('buttons.close')}
          </Button>
          <Button
            onClick={handleSubscribe}
            disabled={isSubscribing}
            loading={isSubscribing}
            loadingText={t('buttons.enabling')}
          >
            {t('buttons.enablePush')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
